import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError, from } from 'rxjs';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { environment } from '../../environments/environment';
import { FirestoreStoreService } from '../_services/firestore-store.service';
import { IndexDbService } from '../_services/index-db.service';

// Track if auth error handling is in progress to prevent duplicate redirects
let isHandlingAuthError = false;

/**
 * HTTP Interceptor that:
 * 1. Attaches Firebase ID token to requests going to Cloudflare API
 * 2. Handles 401/403 errors by redirecting to login
 * 3. Ensures auth token is fresh before making requests
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const afAuth = inject(AngularFireAuth);
  const router = inject(Router);
  const store = inject(FirestoreStoreService);

  // Only attach auth token to Cloudflare API requests
  const isCloudflareApi = req.url.includes(environment.webDomain + '/api');

  if (!isCloudflareApi) {
    // For non-Cloudflare requests, just pass through
    return next(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 || error.status === 403) {
          handleAuthError(router, store);
        }
        return throwError(() => error);
      })
    );
  }

  // For Cloudflare API requests, attach Firebase ID token
  return from(afAuth.currentUser).pipe(
    switchMap(user => {
      if (!user) {
        // No user logged in, redirect to login
        handleAuthError(router, store);
        return throwError(() => new Error('User not authenticated'));
      }

      // Get fresh ID token (automatically refreshes if expired)
      return from(user.getIdToken(false)).pipe(
        switchMap(token => {
          // Clone request and add Authorization header
          const authReq = req.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`
            }
          });

          // Continue with the authenticated request
          return next(authReq);
        }),
        catchError(err => {
          console.error('Failed to get ID token:', err);
          handleAuthError(router, store);
          return throwError(() => err);
        })
      );
    }),
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 || error.status === 403) {
        console.error('Authentication failed:', error.error?.message || error.message);
        handleAuthError(router, store);
      } else if (error.message === 'User not authenticated') {
        // Already handled above
      } else {
        console.error('Failed to get current user:', error);
        handleAuthError(router, store);
      }
      return throwError(() => error);
    })
  );
};

/**
 * Handle authentication errors by clearing session and redirecting to login
 * Now properly sequences cleanup operations before navigation
 */
function handleAuthError(router: Router, store: FirestoreStoreService): void {
  // Prevent duplicate handling
  if (isHandlingAuthError) {
    console.log('[AuthInterceptor] Auth error handling already in progress, skipping...');
    return;
  }

  isHandlingAuthError = true;
  console.warn('[AuthInterceptor] Authentication error detected - cleaning up and redirecting to login');

  // Execute cleanup asynchronously but don't block navigation
  (async () => {
    try {
      // Step 1: Clear all storage synchronously
      localStorage.clear();
      sessionStorage.clear();

      // Step 2: Clear Firestore store signals and listeners
      store.destroy();

      // Step 3: Clear IndexedDB using the proper static method
      // This properly awaits all database deletions
      await IndexDbService.deleteAllDatabases();

      console.log('[AuthInterceptor] Cleanup complete');
    } catch (error) {
      console.error('[AuthInterceptor] Error during cleanup:', error);
      // Continue with navigation even if cleanup fails
    } finally {
      // Reset the flag after a delay to allow for potential retries
      setTimeout(() => {
        isHandlingAuthError = false;
      }, 2000);
    }
  })();

  // Navigate immediately - cleanup happens in parallel
  // This prevents the user from being stuck on a broken page
  router.navigate(['/login'], {
    queryParams: { expired: 'true' },
    replaceUrl: true
  });
}
